/**
 * WHAT THE CLIENT SENT (R10.2, ADR-0015 §R10).
 *
 * The client's file arrived through a link as THEIR response on this request.
 * It answers nothing yet: Leveri proposes it answers the request it was sent
 * for, and a person decides — confirming it, and saying which other open
 * requests of this work the same file also answers. Confirming goes through
 * the one governed gate; the work resumes when nothing is left open.
 *
 * Everything printed here is the server's words (the browser law).
 */
import { useState } from "react";
import { WorkActionResponse, type OpenRequest, type WorkFile } from "@leveri/contracts";
import { Button, Citation, Label, Notice, RecordName } from "../../file/index.js";
import { call, failureMessage } from "../../api.js";
import { useKeyring } from "./keys.js";

export interface ConfirmClientProps {
  workId: string;
  request: OpenRequest;
  /** The work's OTHER open requests, which the same file may also answer. */
  others: readonly OpenRequest[];
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
}

export function ConfirmClientResponses({ workId, request, others, onWork, onOutcome }: ConfirmClientProps) {
  const [also, setAlso] = useState<readonly string[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();

  if (request.clientResponses.length === 0) return null;

  const toggle = (id: string) => setAlso((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  async function confirm(responseId: string) {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await call("POST", `/api/work/${workId}/requests/${request.requirementId}/confirm`, WorkActionResponse, {
        responseId, alsoRequirementIds: also, submissionKey: keyFor(`confirm:${responseId}`),
      });
      onWork(result.work);
      onOutcome(result.outcome);
    } catch (err) {
      setFailure(failureMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 border-t border-rule pt-4" data-testid="client-responses">
      <Label>The client sent</Label>
      {request.clientResponses.map((response) => (
        <div key={response.responseId} className="mt-2">
          <div className="text-sm text-ink">
            {response.files.map((f) => <RecordName key={f.evidenceReferenceId}>{f.filename}</RecordName>)}
          </div>
          <Citation>{response.fromText} · {response.receivedAtText}</Citation>
          <p className="mt-2 text-[13px] text-ink-soft">{response.proposalText}</p>
          {others.length > 0 && (
            <fieldset className="mt-2 flex flex-col gap-1">
              <legend className="text-[13px] text-stone">It also answers:</legend>
              {others.map((other) => (
                <label key={other.requirementId} className="flex items-start gap-2 text-[13px] text-ink">
                  <input type="checkbox" className="mt-0.5" checked={also.includes(other.requirementId)} onChange={() => toggle(other.requirementId)} />
                  <span>{other.questionPlain ?? other.question}</span>
                </label>
              ))}
            </fieldset>
          )}
          <div className="mt-3">
            <Button size="sm" disabled={busy} onClick={() => void confirm(response.responseId)} data-testid="client-response-confirm">
              {busy ? "Confirming…" : also.length ? `Confirm it answers these ${also.length + 1} requests` : "Confirm it answers this request"}
            </Button>
          </div>
        </div>
      ))}
      {failure && <Notice className="mt-3">{failure}</Notice>}
    </div>
  );
}
